import React, { useState, Fragment } from 'react';
import { archiveUserAccount } from '../../../services/firebase';
import { toast } from 'react-hot-toast';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ArchiveBoxIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { classNames } from '../../../utils/formattingUtils';
import CandidateProfileTab from './CandidateProfileTab';
import CandidateJobsTab from './CandidateJobsTab';
import CandidateNotesTab from './CandidateNotesTab';
import CandidateActivityTab from './CandidateActivityTab';

export default function CandidateDetailView({ user, isLoading, onClose }) {
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  const tabs = [
    { name: 'Profile', id: 'profile', current: activeTab === 'profile' },
    { name: 'Jobs', id: 'jobs', current: activeTab === 'jobs' },
    { name: 'Activity', id: 'activity', current: activeTab === 'activity' },
    { name: 'Notes', id: 'notes', current: activeTab === 'notes' },
  ];

  if (!user) return null;
  
  const handleArchiveClick = () => {
    setShowArchiveConfirm(true);
  };

  const confirmArchive = async () => {
    setIsArchiving(true);
    try {
      const result = await archiveUserAccount(user.id);
      
      if (result.success) {
        toast.success('User archived successfully');
        onClose(true); // Pass true to indicate a user was archived
      } else {
        toast.error(`Failed to archive user: ${result.error}`);
      }
    } catch (error) {
      console.error('Error in archive operation:', error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsArchiving(false);
      setShowArchiveConfirm(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Transition.Root show={!!user} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => onClose()}>
          <div className="fixed inset-0" />
          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-2xl">
                    <div className="flex h-full flex-col overflow-y-scroll bg-white dark:bg-gray-900 shadow-xl">
                      <div className="px-6 py-6 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                          <Dialog.Title className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                            Loading User...
                          </Dialog.Title>
                          <div className="flex items-center">
                            <button
                              type="button"
                              className="rounded-md bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              onClick={() => onClose()}
                            >
                              <span className="sr-only">Close panel</span>
                              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="animate-pulse flex flex-col space-y-4 p-6">
                        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                        <div className="space-y-2">
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
                        </div>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    );
  }

  return (
    <>
      <Transition.Root show={!!user} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => onClose()}>
          <div className="fixed inset-0" />
          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-2xl">
                    <div className="flex h-full flex-col overflow-y-scroll bg-white dark:bg-gray-900 shadow-xl">
                      <div className="px-6 py-6 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                          <Dialog.Title className="text-xl font-semibold text-gray-900 dark:text-white">
                            {user.displayName === null ? 'Candidate' : 
                              (typeof user.displayName === 'object' ? (user.displayName.text || 'Candidate') : user.displayName || 'Candidate')}
                          </Dialog.Title>
                          <div className="flex items-center">
                            <button
                              type="button"
                              className="rounded-md bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              onClick={() => onClose()}
                            >
                              <span className="sr-only">Close panel</span>
                              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-6 p-6 flex-grow">
                        {/* User Data Sources Badges */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {user.hasUserProfile && (
                            <span className="inline-flex items-center rounded-md bg-blue-50 dark:bg-blue-900 px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 ring-1 ring-inset ring-blue-600/20 dark:ring-blue-500/30">
                              User Profile
                            </span>
                          )}
                          {user.hasProfileSummary && (
                            <span className="inline-flex items-center rounded-md bg-green-50 dark:bg-green-900 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-300 ring-1 ring-inset ring-green-600/20 dark:ring-green-500/30">
                              Profile Summary
                            </span>
                          )}
                          {user.hasWaitlistData && (
                            <span className="inline-flex items-center rounded-md bg-purple-50 dark:bg-purple-900 px-2 py-1 text-xs font-medium text-purple-700 dark:text-purple-300 ring-1 ring-inset ring-purple-600/20 dark:ring-purple-500/30">
                              Waitlist Data
                            </span>
                          )}
                          {user.isOrphaned && (
                            <span className="inline-flex items-center rounded-md bg-red-50 dark:bg-red-900 px-2 py-1 text-xs font-medium text-red-700 dark:text-red-300 ring-1 ring-inset ring-red-600/20 dark:ring-red-500/30">
                              Orphaned
                            </span>
                          )}
                          {user.isAdmin && (
                            <span className="inline-flex items-center rounded-md bg-amber-50 dark:bg-amber-900 px-2 py-1 text-xs font-medium text-amber-700 dark:text-amber-300 ring-1 ring-inset ring-amber-600/20 dark:ring-amber-500/30">
                              Admin
                            </span>
                          )}
                          {user.isArchived && (
                            <span className="inline-flex items-center rounded-md bg-gray-50 dark:bg-gray-700 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 ring-1 ring-inset ring-gray-600/20 dark:ring-gray-500/30">
                              Archived
                            </span>
                          )}
                        </div>

                        {/* Tabs UI */}
                        <div>
                          <div className="grid grid-cols-1 sm:hidden">
                            <select
                              value={activeTab}
                              onChange={(e) => setActiveTab(e.target.value)}
                              aria-label="Select a tab"
                              className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white dark:bg-gray-800 py-2 pl-3 pr-8 text-base text-gray-900 dark:text-gray-100 outline outline-1 -outline-offset-1 outline-gray-300 dark:outline-gray-600 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 dark:focus:outline-indigo-500"
                            >
                              {tabs.map((tab) => (
                                <option key={tab.id} value={tab.id}>{tab.name}</option>
                              ))}
                            </select>
                            <ChevronDownIcon
                              aria-hidden="true"
                              className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end fill-gray-500 dark:fill-gray-400"
                            />
                          </div>
                          <div className="hidden sm:block">
                            <div className="border-b border-gray-200 dark:border-gray-700">
                              <nav aria-label="Tabs" className="-mb-px flex space-x-8">
                                {tabs.map((tab) => (
                                  <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    aria-current={tab.current ? 'page' : undefined}
                                    className={classNames(
                                      tab.current
                                        ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600 hover:text-gray-700 dark:hover:text-gray-300',
                                      'whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium focus:outline-none'
                                    )}
                                  >
                                    {tab.name}
                                  </button>
                                ))}
                              </nav>
                            </div>
                          </div>
                        </div>

                        {/* Tab Content */}
                        <div className="mt-4">
                          {activeTab === 'profile' && <CandidateProfileTab user={user} />}
                          {activeTab === 'jobs' && <CandidateJobsTab user={user} />}
                          {activeTab === 'activity' && <CandidateActivityTab user={user} />}
                          {activeTab === 'notes' && <CandidateNotesTab user={user} />}
                        </div>
                      </div>

                     
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Archive Confirmation Modal */}
      <Transition.Root show={showArchiveConfirm} as={Fragment}>
        <Dialog as="div" className="relative z-20" onClose={() => setShowArchiveConfirm(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-20 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900">
                      <ArchiveBoxIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                        Archive User
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Are you sure you want to archive this user? This action will disable the user account and hide it from active listings, but the data will be preserved.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-amber-600 dark:bg-amber-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-500 dark:hover:bg-amber-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600 dark:focus-visible:outline-amber-500 sm:col-start-2"
                      onClick={confirmArchive}
                      disabled={isArchiving}
                    >
                      {isArchiving ? 'Archiving...' : 'Archive User'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:col-start-1 sm:mt-0"
                      onClick={() => setShowArchiveConfirm(false)}
                      disabled={isArchiving}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
} 